import { Check } from 'lucide-react';
import { useId, type InputHTMLAttributes, type ReactNode } from 'react';

import { cn } from '../lib/cn';
import { FIELD_BORDER } from '../lib/recipes';

interface ChoiceBaseProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label: ReactNode;
  /** Secondary line under the label, e.g. the consent wording on /payment. */
  description?: ReactNode;
}

/**
 * `max-lg:size-6` (M13.7, WCAG 2.5.8 Target Size Minimum): the painted box is 18px, which
 * is the funnel's proportion and stays so on a pointer. On a touch layout the box grows to
 * the 24px floor.
 *
 * M11.11 grew the *label* to 24px instead and that is enough wherever the label is
 * visible — but `plans-matrix.tsx` and every other matrix cell pass an `sr-only` label,
 * and an `sr-only` label is a 1px box, so those controls were an 18x18 target with
 * nothing to grow. Sizing the input itself is the only fix that covers both.
 */
const BOX = cn(
  'peer size-[18px] max-lg:size-6 shrink-0 appearance-none bg-surface',
  FIELD_BORDER,
  'transition-colors duration-150',
  'hover:border-primary-hover',
  /* Q-P15: the checked box carries a white tick / a white dot, so it is a
     white-on-primary surface and takes the accessible blue — which also lifts the box's
     own boundary past the 3:1 non-text floor (WCAG 1.4.11). */
  'checked:border-primary-accessible checked:bg-primary-accessible checked:hover:border-primary-accessible',
  'disabled:cursor-not-allowed disabled:border-neutral-200 disabled:bg-neutral-100',
);

const ROW = 'flex items-start gap-[10px] text-base text-ink';

export type CheckboxProps = ChoiceBaseProps;

/**
 * A native checkbox with its own box drawn on top, so the 1.5px border and 4px radius
 * match every other surface. `appearance-none` keeps the real input in the tab order
 * and in the form's data — only the paint changes (rules §48.2).
 */
export function Checkbox({ label, description, className, id, ...rest }: CheckboxProps) {
  const generated = useId();
  const controlId = id ?? generated;
  const descriptionId = description === undefined ? undefined : `${controlId}-description`;

  return (
    <div className={cn(ROW, className)}>
      <span className="relative inline-flex shrink-0 items-center pt-[2px]">
        <input
          type="checkbox"
          id={controlId}
          aria-describedby={rest['aria-describedby'] ?? descriptionId}
          className={cn(BOX, 'rounded-sm')}
          {...rest}
        />
        <Check
          size={12}
          strokeWidth={3}
          aria-hidden="true"
          /* Centred in both box sizes: (18-12)/2 and (24-12)/2. */
          className="pointer-events-none absolute left-[3px] max-lg:left-[6px] text-primary-contrast opacity-0 peer-checked:opacity-100"
        />
      </span>
      <span className="flex flex-col">
        <label
          htmlFor={controlId}
          /* `max-lg:min-h-6` (M11.11): the label is part of the control's target, and
             a 21px line box is under WCAG 2.5.8's 24px floor on a touch layout. The
             extra 3px go below the text, so nothing shifts. */
          className="block cursor-pointer leading-[21px] max-lg:min-h-6"
        >
          {label}
        </label>
        {description === undefined ? null : (
          <span id={descriptionId} className="mt-[2px] text-sm text-neutral-500">
            {description}
          </span>
        )}
      </span>
    </div>
  );
}

export type RadioProps = ChoiceBaseProps;

/** The checkbox recipe at `--radius-full` with a filled dot instead of a tick. */
export function Radio({ label, description, className, id, ...rest }: RadioProps) {
  const generated = useId();
  const controlId = id ?? generated;
  const descriptionId = description === undefined ? undefined : `${controlId}-description`;

  return (
    <div className={cn(ROW, className)}>
      <span className="relative inline-flex shrink-0 items-center pt-[2px]">
        <input
          type="radio"
          id={controlId}
          aria-describedby={rest['aria-describedby'] ?? descriptionId}
          className={cn(BOX, 'rounded-full')}
          {...rest}
        />
        <span
          aria-hidden="true"
          /* Centred in both box sizes: (18-6)/2 and (24-6)/2. */
          className="pointer-events-none absolute left-[6px] max-lg:left-[9px] size-[6px] rounded-full bg-primary-contrast opacity-0 peer-checked:opacity-100"
        />
      </span>
      <span className="flex flex-col">
        <label
          htmlFor={controlId}
          /* `max-lg:min-h-6` (M11.11): the label is part of the control's target, and
             a 21px line box is under WCAG 2.5.8's 24px floor on a touch layout. The
             extra 3px go below the text, so nothing shifts. */
          className="block cursor-pointer leading-[21px] max-lg:min-h-6"
        >
          {label}
        </label>
        {description === undefined ? null : (
          <span id={descriptionId} className="mt-[2px] text-sm text-neutral-500">
            {description}
          </span>
        )}
      </span>
    </div>
  );
}
